import React, { useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { AssetPmTaskResults } from './AssetPmTaskResults';
import { AssetMaintenanceDepartureGuard } from './AssetMaintenanceDepartureGuard';
import { TpmStatusBadge } from '../Tooling/TpmMaintenanceList';

function formatDate(value) {
    if (!value) return '—';

    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
}

function InfoCell({ label, value }) {
    return (
        <Col sm={12} md={4} className="mb-2">
            <div style={{ border: '1px solid #d7dce5', padding: '8px 10px', minHeight: 44 }}>
                <strong>{label}: </strong>{value == null || value === '' ? '—' : value}
            </div>
        </Col>
    );
}

export function AssetPmRecordForm({ detail, slug, kind, title, onClose, onReopen }) {
    const [saved, setSaved] = useState(detail);
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState(null);
    const [decision, setDecision] = useState(null);
    const [reason, setReason] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const isClosed = saved.status === 'Closed';
    const incomplete = saved.tasks.some((task) => task.description?.trim() && !task.completed);

    function resetDecision() {
        setDecision(null);
        setReason('');
        setConfirmed(false);
    }

    async function perform(action) {
        if (inFlight.current) return;

        inFlight.current = true;
        setBusy(true);
        setError(null);
        try {
            const result = action === 'reopen'
                ? await onReopen({ revision: saved.revision, reason, source: 'web' })
                : await onClose({
                    revision: saved.revision,
                    allowIncomplete: action === 'incomplete',
                    reason: action === 'incomplete' ? reason : null,
                    source: 'web',
                });
            setSaved(result);
            setMessage(action === 'reopen' ? 'Maintenance reopened' : 'Maintenance closed');
            resetDecision();
        } catch (failure) {
            setError(failure);
            setMessage('Unable to update maintenance status.');
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    }

    const request = saved.request;
    const asset = saved.asset;
    return (
        <>
            <AssetMaintenanceDepartureGuard blocked={busy || Boolean(reason)} busy={busy} />
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h1 className="h3 mb-0">{title} Maintenance — {saved.frequency}</h1>
                <div className="d-flex gap-2 align-items-center">
                    <TpmStatusBadge status={saved.status} />
                    <Button
                        variant={isClosed ? 'warning' : 'success'}
                        disabled={busy || !!error?.latest}
                        onClick={() => setDecision(isClosed ? 'reopen' : incomplete ? 'incomplete' : 'close')}
                    >
                        {busy ? 'Saving…' : isClosed ? 'Reopen' : 'Mark as Closed'}
                    </Button>
                    <Button as={Link} variant="outline-secondary" to={`/tpm/${slug}/preventive-maintenance?view=records`}>
                        Back
                    </Button>
                </div>
            </div>
            {error && (
                <Alert variant="danger">
                    {error.message}
                    {Object.entries(error.fields || {}).map(([field, text]) => (
                        <div key={field}>{field}: {text}</div>
                    ))}
                    {error.latest && (
                        <Button variant="outline-danger" disabled={busy} onClick={() => {
                            setSaved(error.latest);
                            setError(null);
                            resetDecision();
                            setMessage('Latest saved record loaded');
                        }}>
                            Load latest saved record
                        </Button>
                    )}
                </Alert>
            )}
            <Row>
                <InfoCell label="Asset" value={asset.name || asset.machine || asset.testerNo || asset.jigFixNo} />
                <InfoCell label="Frequency" value={saved.frequency} />
                <InfoCell label="Repair Ticket" value={request && (
                    <>
                        <Link to={`/tpm/${slug}/summary/requests/${request.id}`}>{request.ticketId}</Link>
                        {request.processStatus && ` · ${request.processStatus}`}
                    </>
                )} />
                <InfoCell label="Due Date" value={saved.dueDate} />
                <InfoCell label="Due State" value={`${saved.dueState}${saved.daysOverdue ? ` (${saved.daysOverdue} days overdue)` : ''}`} />
                <InfoCell label="Created" value={formatDate(saved.createdAt || saved.created_at)} />
            </Row>
            {isClosed && (
                <Row>
                    <InfoCell label="Closed By" value={saved.closedByUsername || (saved.closedBy ? `User ${saved.closedBy}` : null)} />
                    <InfoCell label="Closed At" value={formatDate(saved.closedAt)} />
                    <InfoCell label="Closed From" value={saved.closedSource} />
                </Row>
            )}
            <h5 style={{ marginTop: 14 }}>Maintenance Tasks</h5>
            <div className="text-muted mb-2">
                Task completion is recorded on the mobile app. This view is read-only apart from the status.
            </div>
            <AssetPmTaskResults tasks={saved.tasks} kind={kind} recordId={saved.id} />
            {message && <p role="status" aria-live="polite">{message}</p>}
            {!!saved.audit?.length && (
                <details>
                    <summary>Maintenance audit history</summary>
                    <ul>
                        {saved.audit.map((event, index) => (
                            <li key={index}>
                                {event.action} · {formatDate(event.at)}
                                {event.actor ? ` · ${event.actorUsername || `User ${event.actor}`}` : ''}
                                {event.reason ? ` · ${event.reason}` : ''}
                                {event.skippedPeriods ? ` · ${event.skippedPeriods} periods skipped; next due ${event.nextDueDate}` : ''}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
            <Modal show={!!decision} onHide={() => { if (!busy) resetDecision(); }} backdrop="static">
                <Modal.Header closeButton={!busy}>
                    <Modal.Title>{decision === 'reopen' ? 'Reopen maintenance' : 'Close maintenance'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {decision === 'close' ? <p>Close this maintenance record?</p> : (
                        <>
                            {decision === 'incomplete' && <p>Some tasks are not complete. Provide a reason to close this record with incomplete work.</p>}
                            <Form.Label htmlFor="pm-decision-reason">Decision reason</Form.Label>
                            <Form.Control
                                id="pm-decision-reason"
                                as="textarea"
                                value={reason}
                                disabled={busy}
                                maxLength={4000}
                                onChange={(event) => setReason(event.target.value)}
                            />
                            {decision === 'incomplete' && (
                                <Form.Check
                                    id="pm-confirm-incomplete"
                                    className="mt-2"
                                    label="I confirm described tasks remain incomplete"
                                    checked={confirmed}
                                    disabled={busy}
                                    onChange={(event) => setConfirmed(event.target.checked)}
                                />
                            )}
                        </>
                    )}
                    {error && <Alert variant="danger" className="mt-2">{error.message}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" disabled={busy} onClick={resetDecision}>Cancel</Button>
                    <Button
                        variant={decision === 'close' ? 'success' : 'warning'}
                        disabled={
                            busy ||
                            !!error?.latest ||
                            (decision !== 'close' && !reason.trim()) ||
                            (decision === 'incomplete' && !confirmed)
                        }
                        onClick={() => perform(decision)}
                    >
                        {busy ? 'Saving…' : decision === 'reopen' ? 'Reopen maintenance' : decision === 'incomplete' ? 'Confirm incomplete close' : 'Close maintenance'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
